/**
 * The canonical BookingStatus + BookingChannel vocabulary and the FIXED platform status-colour
 * system (reservation-dashboard-ui, DOMAIN.md §C.7/§B.6). Colour lives in `@repo/theme` as
 * `booking.*` semantic tokens; this module is the single source that maps each status to its token,
 * its i18n label key, and a NON-COLOUR redundancy (a shape glyph) — RGAA/WCAG forbids colour-only
 * meaning (ADR-0023), so a badge always carries a text label AND a shape, never hue alone.
 *
 * It also owns the affordance matrix (which actions a booking exposes per status — R1 / BK-8): no
 * cancel on a SEATED booking, terminal states offer only "recreate", and a waiter cancel of a live
 * booking needs the manager step-up. Presentational components read this; the server re-enforces.
 */

export const BOOKING_STATUSES = [
  "PENDING",
  "CONFIRMED",
  "SEATED",
  "COMPLETED",
  "NO_SHOW",
  "CANCELLED",
] as const;
export type BookingStatus = (typeof BOOKING_STATUSES)[number];

/** The canonical ten channels (§B.6) — availability owns them; the dashboard only tags/filters. */
export const BOOKING_CHANNELS = [
  "SUBDOMAIN",
  "EMBED",
  "PHONE",
  "WALK_IN",
  "WAITLIST",
  "GOOGLE",
  "META",
  "MARKETPLACE",
  "INTEGRATOR",
  "QR",
] as const;
export type BookingChannel = (typeof BOOKING_CHANNELS)[number];

/** Channels a staff member may pick for MANUAL entry (backend rejects anything else, §C.7 walk-in). */
export const MANUAL_ENTRY_CHANNELS: readonly BookingChannel[] = ["PHONE", "WALK_IN"];

const STATUS_SET = new Set<string>(BOOKING_STATUSES);
const CHANNEL_SET = new Set<string>(BOOKING_CHANNELS);

/** Narrow a wire string to a known BookingStatus, or `null` when unmapped. */
export function toBookingStatus(value: string | null | undefined): BookingStatus | null {
  return value && STATUS_SET.has(value) ? (value as BookingStatus) : null;
}

/** Narrow a wire string to a known BookingChannel, or `null` — an unmapped value is surfaced as
 *  "needs channel tagging", never silently defaulted (R6 edge case "channel not yet mapped"). */
export function toBookingChannel(value: string | null | undefined): BookingChannel | null {
  return value && CHANNEL_SET.has(value) ? (value as BookingChannel) : null;
}

export interface StatusVisual {
  /** `@repo/theme` `booking.*` semantic-token base (`.bg`/`.fg`/`.solid`/`.border` leaves). */
  token: string;
  /** i18n key for the status label — the primary, colour-independent meaning carrier. */
  labelKey: string;
  /** A shape glyph doubling the colour (RGAA redundancy) — distinct per status. */
  shape: string;
}

const STATUS_VISUAL: Record<BookingStatus, StatusVisual> = {
  PENDING: { token: "booking.pending", labelKey: "bookings.status.pending", shape: "◆" },
  CONFIRMED: { token: "booking.confirmed", labelKey: "bookings.status.confirmed", shape: "●" },
  SEATED: { token: "booking.seated", labelKey: "bookings.status.seated", shape: "▲" },
  COMPLETED: { token: "booking.completed", labelKey: "bookings.status.completed", shape: "✓" },
  NO_SHOW: { token: "booking.noShow", labelKey: "bookings.status.noShow", shape: "✕" },
  CANCELLED: { token: "booking.cancelled", labelKey: "bookings.status.cancelled", shape: "○" },
};

export function statusVisual(status: BookingStatus): StatusVisual {
  return STATUS_VISUAL[status];
}

/** i18n label key for a channel (known values only; unmapped handled by the caller). */
export function channelLabelKey(channel: BookingChannel): string {
  return `bookings.channel.${channel}`;
}

export interface Affordances {
  /** Reschedule / move / change party — only on live, non-seated bookings (BK-7). */
  canEdit: boolean;
  /** Cancel — never on SEATED (dashboard-cancel forbidden, BK-8); waiter needs step-up (below). */
  canCancel: boolean;
  /** Terminal rows offer only "recreate" (a fresh booking pre-filled from this one). */
  canRecreate: boolean;
  isTerminal: boolean;
  isPending: boolean;
}

const TERMINAL: ReadonlySet<BookingStatus> = new Set(["COMPLETED", "NO_SHOW", "CANCELLED"]);

/** The action affordances a status exposes (R1: affordances DERIVE from status). */
export function affordancesFor(status: BookingStatus): Affordances {
  const isTerminal = TERMINAL.has(status);
  const isPending = status === "PENDING";
  const isLiveEditable = status === "CONFIRMED" || status === "PENDING";
  return {
    canEdit: isLiveEditable,
    // SEATED is explicitly never dashboard-cancellable (SEATED_NOT_CANCELLABLE); terminal states
    // are already closed. Only live (CONFIRMED/PENDING) bookings can be cancelled.
    canCancel: isLiveEditable,
    canRecreate: isTerminal,
    isTerminal,
    isPending,
  };
}

/**
 * Whether cancelling requires the manager-PIN step-up (BK-8 / ST-4). A waiter cancelling a live
 * booking must present a `CANCEL_CONFIRMED_BOOKING` authorization; a manager (or account root) does
 * not. Mirrors the backend `StepUpEnforcementInterceptor` (which checks the actor's permission, not
 * the booking status) — so the UI prompts a waiter for any cancel, a manager for none.
 */
export function cancelNeedsStepUp(isManagerOrAbove: boolean): boolean {
  return !isManagerOrAbove;
}

/**
 * Whether the no-show charge action is offered for a booking (card-fingerprint-no-show.md R5): ONLY a
 * `NO_SHOW` booking or a cancellation past the cutoff is chargeable — a cancellation within the window
 * is never chargeable, so the action is absent. `lateCancellation` is the server's past-cutoff verdict
 * (absent from the summary row today → charge is offered on `NO_SHOW` alone); the server re-enforces
 * eligibility and returns `NOT_ELIGIBLE` otherwise. The charge itself is always manager-step-up-gated
 * (`@RequiresStepUp(NO_SHOW_CHARGE)`, R6) — this predicate only decides visibility.
 */
export function canChargeNoShowFee(
  status: BookingStatus | null,
  lateCancellation = false,
): boolean {
  return status === "NO_SHOW" || (status === "CANCELLED" && lateCancellation);
}
